import { useEffect, useState } from "react";

import { api } from "../lib/api";
import { identifierAsHex } from "../lib/identifiers";

interface Diag {
  id: number;
  identifier: string;
  lat: number;
  lon: number;
  connected: boolean;
  station_altitude_ellipsoid_m: number;
  dem_orthometric_m: number | null;
  geoid_n_m: number | null;
  dem_ellipsoidal_m: number | null;
  dem_source: string;
  derived_agl_m: number;
  agl_derivation: string;
  agl_override_m: number | null;
  agl_override_set_at: string | null;
  effective_agl_m: number;
}

const M_TO_FT = 3.2808;

export function StationDiagPopup({
  stationId,
  onClose,
}: {
  stationId: number;
  onClose: () => void;
}) {
  const [diag, setDiag] = useState<Diag | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [overrideFt, setOverrideFt] = useState<string>("");
  const [busy, setBusy] = useState(false);

  // Initial load + reload after save
  const load = () => {
    setError(null);
    api.stationDiagnostics(stationId)
      .then((d) => {
        setDiag(d);
        setOverrideFt(d.agl_override_m !== null ? (d.agl_override_m * M_TO_FT).toFixed(1) : "");
      })
      .catch((e) => setError(`Load failed: ${e.message}`));
  };
  useEffect(load, [stationId]);

  const save = async () => {
    if (!diag) return;
    const ft = parseFloat(overrideFt);
    if (!Number.isFinite(ft) || ft < 0 || ft > 1000) {
      setError("AGL must be a non-negative number, < 1000 ft");
      return;
    }
    const meters = ft / M_TO_FT;
    setBusy(true);
    setError(null);
    try {
      await api.setAglOverride(stationId, meters);
      load();
    } catch (e: any) {
      setError(`Save failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const clearOverride = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.setAglOverride(stationId, null);
      load();
    } catch (e: any) {
      setError(`Clear failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={hdr}>
          <span style={{ fontFamily: "monospace", fontSize: 16 }}>
            {diag ? identifierAsHex(diag.identifier) : `…${stationId}`}
          </span>
          <button style={closeBtn} onClick={onClose} title="Close">×</button>
        </div>

        {error && <div style={errBox}>{error}</div>}
        {!diag && !error && <div style={{ padding: 16, color: "#888" }}>Loading…</div>}

        {diag && (<>
          <div style={section}>
            <Row k="Identifier (decimal)" v={diag.identifier} mono />
            <Row k="Lat / lon" v={`${diag.lat.toFixed(5)}, ${diag.lon.toFixed(5)}`} mono />
            <Row k="Connected" v={diag.connected ? "yes" : "no"} />
            <Row k="DEM source" v={diag.dem_source} />
          </div>

          <div style={sectionTitle}>Vertical reference</div>
          <div style={section}>
            <Row k="Station GPS alt" v={fmtMFt(diag.station_altitude_ellipsoid_m)}
                 hint="ellipsoidal, from CSV" />
            {diag.dem_orthometric_m != null && (
              <Row k="DEM orthometric" v={fmtMFt(diag.dem_orthometric_m)}
                   hint="ground level above local mean sea (NAVD88 / EGM2008)" />
            )}
            {diag.geoid_n_m != null && (
              <Row k="Geoid N" v={fmtMFt(diag.geoid_n_m)}
                   hint="ellipsoidal − orthometric (negative across CONUS)" />
            )}
            {diag.dem_ellipsoidal_m != null && (
              <Row k="DEM ellipsoidal" v={fmtMFt(diag.dem_ellipsoidal_m)}
                   hint="ground level above WGS84 ellipsoid" />
            )}
          </div>

          <div style={sectionTitle}>Antenna AGL</div>
          <div style={section}>
            <Row k="Derived AGL" v={fmtMFt(diag.derived_agl_m)}
                 hint={`station GPS − DEM ellipsoidal (${diag.agl_derivation})`} />
            {diag.agl_override_m !== null && (
              <Row k="Override" v={fmtMFt(diag.agl_override_m)}
                   hint={`set ${diag.agl_override_set_at?.slice(0, 19) ?? ""}`}
                   highlight />
            )}
            <Row k="Effective AGL" v={fmtMFt(diag.effective_agl_m)}
                 hint="value the next viewshed compute will use"
                 strong />
          </div>

          <div style={sectionTitle}>Override</div>
          <div style={{ ...section, display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="number"
              step="0.5"
              min="0"
              max="1000"
              value={overrideFt}
              onChange={(e) => { setOverrideFt(e.target.value); setError(null); }}
              placeholder="ft AGL"
              style={input}
              disabled={busy}
            />
            <span style={{ fontSize: 12, color: "#888" }}>ft</span>
            <button style={primary} onClick={save} disabled={busy || !overrideFt}>
              {busy ? "…" : "Save"}
            </button>
            {diag.agl_override_m !== null && (
              <button style={ghost} onClick={clearOverride} disabled={busy} title="Revert to derived value">
                Reset
              </button>
            )}
          </div>
          <p style={{ fontSize: 11, color: "#888", margin: "0 12px 12px" }}>
            Override survives CSV re-ingest. Next viewshed run that includes
            this station will pick up the new value (cache-miss for this
            observer; other stations stay cached).
          </p>
        </>)}
      </div>
    </div>
  );
}

function fmtMFt(m: number): string {
  return `${m.toFixed(2)} m  /  ${(m * M_TO_FT).toFixed(1)} ft`;
}

function Row({ k, v, mono, hint, strong, highlight }: {
  k: string;
  v: string;
  mono?: boolean;
  hint?: string;
  strong?: boolean;
  highlight?: boolean;
}) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", padding: "6px 0",
      borderTop: "1px solid #1a1a1a",
      ...(highlight ? { background: "#1a2030", margin: "0 -12px", padding: "6px 12px" } : null),
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <span style={{ fontSize: 11, color: "#888" }}>{k}</span>
        <span style={{
          fontSize: 12,
          fontFamily: mono ? "monospace" : "inherit",
          color: strong ? "#7cf" : "#ddd",
          fontWeight: strong ? 600 : 400,
        }}>
          {v}
        </span>
      </div>
      {hint && <span style={{ fontSize: 10, color: "#666", marginTop: 2 }}>{hint}</span>}
    </div>
  );
}

const backdrop: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100,
};
const panel: React.CSSProperties = {
  background: "#0f0f0f", color: "#e5e5e5",
  border: "1px solid #2a2a2a", borderRadius: 6,
  width: 420, maxWidth: "90vw", maxHeight: "90vh", overflowY: "auto",
};
const hdr: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "10px 12px", borderBottom: "1px solid #222",
};
const closeBtn: React.CSSProperties = {
  background: "transparent", border: 0, color: "#aaa", fontSize: 22,
  cursor: "pointer", lineHeight: 1, padding: "0 4px",
};
const section: React.CSSProperties = { padding: "0 12px 12px" };
const sectionTitle: React.CSSProperties = {
  fontSize: 11, textTransform: "uppercase", letterSpacing: 1,
  color: "#999", padding: "10px 12px 6px",
};
const errBox: React.CSSProperties = {
  background: "#3a1a1a", color: "#fcc", padding: "8px 12px",
  margin: "8px 12px", borderRadius: 4, fontSize: 12,
};
const input: React.CSSProperties = {
  flex: 1, background: "#141414", color: "#e5e5e5",
  border: "1px solid #333", padding: "6px 8px", borderRadius: 4, fontSize: 13,
};
const primary: React.CSSProperties = {
  padding: "6px 14px", background: "#3a7bd5", color: "white",
  border: 0, borderRadius: 4, cursor: "pointer", fontSize: 12,
};
const ghost: React.CSSProperties = {
  padding: "6px 14px", background: "transparent", color: "#aaa",
  border: "1px solid #333", borderRadius: 4, cursor: "pointer", fontSize: 12,
};
